import { ICycleConfiguration } from 'typings/cycleConfiguration';
import { Format } from 'nomst/Stores/Utility/format';
import { RoundingStrategy } from 'nomst/Stores/Utility/roundingStrategy';
import {
    CycleStatus,
    PayRangeDisplayType,
    CompaRatioDisplayFormat,
    BaseOfCalculation,
    EligibilityRuleType } from 'typings/enums';
import { Configuration as MobxConfiguration } from 'nomst/Stores/Configuration/configuration';
import { PayRangeConfiguration } from 'nomst/Stores/Configuration/payRangeConfiguration';
import { RatingConfiguration } from 'nomst/Stores/Configuration/ratingConfiguration';
import { PerformanceRating } from 'nomst/Stores/Configuration/performanceRating';
import { ICycleBudget } from 'typings/cycleBudget';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { RangeEnforcement } from 'nomst/Stores/Configuration/rangeEnforcement';
import { TeamPageViews } from 'nomst/Stores/Configuration/teamPageViews';
import { CurrencyTransformer } from 'nomst/Stores/Utility/currencyTransformer';
import { EligibilityRule } from 'nomst/Stores/Configuration/eligibilityRule';
import { ProrationRule } from 'nomst/Stores/Configuration/prorationRule';
import { ICycleBudgetFieldFromService, ICycleConfigurationFromService } from 'typings/cycleConfiguration';
import { IWorkflowConfiguration } from 'typings/workflowConfiguration';
import { ICycleBudgetField } from 'typings/cycleBudgetField';
import { ICycleBudgetFieldValue } from 'typings/cycleBudgetFieldValue';
import { WorkflowConfiguration } from 'nomst/Stores/Configuration/workflowConfiguration';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';
import { EmployeeFormatConfiguration } from './employeeFormatConfiguration';
import { SettingsConfiguration } from 'nomst/Stores/Configuration/settingsConfiguration';

export class ConfigurationFactory {
    // tslint:disable:parameters-max-number
    static getConfiguration(
        masterAccountId: string,
        cycleId: number,
        configuration: SettingsConfiguration,
        employeeFormatConfiguration: EmployeeFormatConfiguration,
        cycleConfiguration: Nullable<ICycleConfiguration>,
        accessToken: string,
        userId: string): MobxConfiguration {
        if (!cycleConfiguration) {
            return {} as MobxConfiguration;
        }

        const cycleBudgetFields = ConfigurationFactory.getCycleBudgetFields(cycleConfiguration, masterAccountId);
        const cycleBudgets = ConfigurationFactory.getCycleBudgets(cycleConfiguration);

        const teamPageViews =
            cycleConfiguration ?
                cycleConfiguration.teamPageViews :
                {
                    tcc: false,
                    excludedTccFields: [] as string[],
                    increaseRecommendation: false,
                    excludedCompSummaryFields: [] as string[],
                    shouldDisplayAllCompSummaryFields: true,
                    hideIneligibleEmployees: false,
                    lockViewForManager: true
                } as ITeamPageViews;

        const internalPayRange =
            cycleConfiguration ?
                cycleConfiguration.internalPayRange :
                {
                    showPayRange: false,
                    displayType: PayRangeDisplayType.RangePenetration,
                    compaRatioDisplayFormat: CompaRatioDisplayFormat.ShowAsDecimal,
                    baseOfCalculation: BaseOfCalculation.BasePay
                };

        const workflowConfiguration = cycleConfiguration
            ? cycleConfiguration.workflowConfiguration
            : {
                rules: []
            };

        const status = cycleConfiguration ? cycleConfiguration.status : '';

        return new MobxConfiguration(
            masterAccountId,
            cycleId,
            status,
            configuration.homeCountryCurrency,
            CurrencyTransformer.transformToAllTargetCurrencies(configuration.currencies),
            configuration.hasMultipleCurrency,
            employeeFormatConfiguration,
            cycleBudgetFields,
            cycleBudgets as ICycleBudget[],
            ConfigurationFactory.getRound(configuration),
            new PayRangeConfiguration(
                internalPayRange.showPayRange,
                internalPayRange.displayType,
                internalPayRange.compaRatioDisplayFormat,
                internalPayRange.baseOfCalculation),
            ConfigurationFactory.getRatingConfiguration(configuration, cycleConfiguration),
            new WorkflowConfiguration(workflowConfiguration, masterAccountId, cycleId),
            new TeamPageViews(teamPageViews),
            accessToken,
            userId,
            this.convertBonusFieldRules(cycleConfiguration),
            cycleConfiguration ? cycleConfiguration.closedDate : null,
            cycleConfiguration.endDate,
            cycleConfiguration.startDate || '',
            cycleConfiguration.payEffectiveDate,
            cycleConfiguration.comment || '',
            cycleConfiguration.previewMode);
    }

    static parseCycleConfiguration(
        cycleConfigurationFromService: ICycleConfigurationFromService): ICycleConfiguration {
        const cycleConfiguration = JSON.parse(cycleConfigurationFromService.value) as ICycleConfiguration;
        cycleConfiguration.id = cycleConfigurationFromService.cycleId;
        cycleConfiguration.cycleId = cycleConfigurationFromService.cycleId;
        cycleConfiguration.startDate = cycleConfigurationFromService.startDate;
        cycleConfiguration.endDate = cycleConfigurationFromService.endDate;
        cycleConfiguration.payEffectiveDate = cycleConfigurationFromService.payEffectiveDate;
        cycleConfiguration.closedDate = cycleConfigurationFromService.closedDate;
        cycleConfiguration.status = cycleConfigurationFromService.status as CycleStatus;
        cycleConfiguration.previewMode = cycleConfigurationFromService.previewMode;
        if (cycleConfigurationFromService.budgetFields) {
            cycleConfiguration.budgetFields =
                ConfigurationFactory.parseCycleBudgetFields(cycleConfigurationFromService.budgetFields);
        }

        if (cycleConfigurationFromService.budgets) {
            cycleConfigurationFromService.budgets.forEach(budget => budget.id = budget.budgetCycleId);
            cycleConfiguration.budgets = cycleConfigurationFromService.budgets;
        }

        cycleConfiguration.workflowConfiguration =
            JSON.parse(cycleConfigurationFromService.workflowConfigurationValue) as IWorkflowConfiguration;
        return cycleConfiguration;
    }

    static parseCycleBudgetFields(
        cycleBudgetFieldsFromService: ICycleBudgetFieldFromService[]
    ): ICycleBudgetField[] {
        return cycleBudgetFieldsFromService.map(cycleBudgetFieldFromService => {
            const cycleBudgetField = cycleBudgetFieldFromService as ICycleBudgetField;
            const cycleBudgetFieldValue = JSON.parse(cycleBudgetFieldFromService.value) as ICycleBudgetFieldValue;
            if (cycleBudgetFieldValue.stock) {
                cycleBudgetField.stockDisplayType = cycleBudgetFieldValue.stock.displayType;
                cycleBudgetField.stockValue = cycleBudgetFieldValue.stock.value;
            }

            return cycleBudgetField;
        }) as ICycleBudgetField[];
    }

    private static getRound(configuration: SettingsConfiguration): Format {
        const annualRoundingStrategy = new RoundingStrategy(
            configuration.generalConfiguration.annualRounding,
            configuration.generalConfiguration.annualNumberAfterDecimal);
        const hourlyRoundingStrategy = new RoundingStrategy(
            configuration.generalConfiguration.hourlyRounding,
            configuration.generalConfiguration.hourlyNumberAfterDecimal);
        const stockRoundingStrategy = new RoundingStrategy(
            configuration.generalConfiguration.stockRounding,
            configuration.generalConfiguration.stockNumberAfterDecimal);
        const percentageRoundingStrategy = new RoundingStrategy(
            configuration.generalConfiguration.percentageRounding,
            configuration.generalConfiguration.percentageNumberAfterDecimal);

        return new Format(
            annualRoundingStrategy,
            hourlyRoundingStrategy,
            percentageRoundingStrategy,
            stockRoundingStrategy);
    }

    private static getCycleBudgetFields(cycleConfiguration: ICycleConfiguration, masterAccountId: string): CycleBudgetField[] {
        if (!cycleConfiguration || !cycleConfiguration.budgetFields) {
            return [];
        }

        return cycleConfiguration.budgetFields.map(budgetField => new CycleBudgetField(
            {
                amountCycleId: budgetField.amountCycleId,
                budgetCycleId: budgetField.budgetCycleId,
                amountCode: budgetField.amountCode,
                eligibilityRule: (budgetField.eligibilityRule && budgetField.eligibilityRule.eligibilityRuleId) ?
                    new EligibilityRule({
                        eligibilityRuleId: budgetField.eligibilityRule.eligibilityRuleId,
                        comment: budgetField.eligibilityRule.comment || '',
                        ruleFilters: budgetField.eligibilityRule.ruleFilters || [],
                        eligibilityRuleType: budgetField.eligibilityRule.eligibilityRuleType || EligibilityRuleType.Annual,
                        editableComment: budgetField.eligibilityRule.editableComment || '',
                        amountCycleIds: budgetField.eligibilityRule.amountCycleIds || [],
                        associatedAmountsTitle: budgetField.eligibilityRule.associatedAmountsTitle || [],
                        isEditMode: budgetField.eligibilityRule.isEditMode
                    }, masterAccountId) : null,
                title: budgetField.title,
                percentageCalculationRule: budgetField.percentageCalculationRule,
                payType: budgetField.payType,
                rangeEnforcement: budgetField.rangeEnforcement ? {
                    useAutoCalculateLumpSum: budgetField.rangeEnforcement.useAutoCalculateLumpSum,
                    allowOverrideLumpSum: budgetField.rangeEnforcement.allowOverrideLumpSum,
                    autoCalculatePercentage: budgetField.rangeEnforcement.autoCalculatePercentage} : {} as RangeEnforcement,
                stockDisplayType: budgetField.stockDisplayType,
                stockValue: budgetField.stockValue,
                prorationRule: budgetField.prorationRule ? new ProrationRule({
                    prorationRuleId: budgetField.prorationRule.prorationRuleId,
                    prorateMethod: budgetField.prorationRule.prorateMethod,
                    prorateMethodId: budgetField.prorationRule.prorateMethodId,
                    ruleFilter: budgetField.prorationRule.ruleFilter,
                    calendarDuration: budgetField.prorationRule.calendarDuration,
                    numberOfDaysInAMonth: budgetField.prorationRule.numberOfDaysInAMonth,
                    numberOfDaysInAWeek: budgetField.prorationRule.numberOfDaysInAWeek,
                    associatedAmountsTitle: budgetField.prorationRule.associatedAmountsTitle,
                }) : null,
                shouldShowOriginalBudget: budgetField.shouldShowOriginalBudget,
                colorPriority: budgetField.colorPriority} as unknown as ICycleBudgetField, masterAccountId));
    }

    private static getCycleBudgets(cycleConfiguration: ICycleConfiguration): CycleBudget[] {
        if (!cycleConfiguration || !cycleConfiguration.budgets) {
            return [];
        }

        return cycleConfiguration.budgets.map(budget => new CycleBudget({
            id: budget.id,
            budgetCycleId: budget.budgetCycleId,
            cycleId: budget.cycleId,
            name: budget.name,
            color: budget.color,
            allowManagerOverBudget: budget.allowManagerOverBudget,
            requireCommentWhenOverBudget: budget.requireCommentWhenOverBudget,
            useProration: budget.useProration,
            associatedFields: budget.associatedFields,
            isBudgetHasAnyDecision: budget.isBudgetHasAnyDecision,
            amountsAlreadyHaveDecisions: budget.amountsAlreadyHaveDecisions,
            cycleBudgetFields: budget.cycleBudgetFields,
            autoSync: budget.autoSync,
            lastSync: budget.lastSync,
            topPriorityField: budget.topPriorityField
        } as ICycleBudget));
    }

    private static convertBonusFieldRules(config: ICycleConfiguration): IBonusFieldRule[] {
        const bonusFieldRules = [] as IBonusFieldRule[];
        if (!config || !config.cycleBonusFieldRules) {
            return [];
        }
        const fields = config.cycleBonusFieldRules;
        fields.forEach(field => {
            const rule = {
                fieldName: field.fieldName,
                fieldIndex: field.fieldIndex,
                split: field.split,
                attained: field.attained
            } as IBonusFieldRule;
            bonusFieldRules.push(rule);
        });
        return bonusFieldRules;
    }

    private static getRatingConfiguration(
        configuration: Nullable<SettingsConfiguration>,
        cycleConfiguration: ICycleConfiguration): Nullable<RatingConfiguration> {
        if (!configuration || !configuration.performanceRatings ||
            !cycleConfiguration || !cycleConfiguration.ratingConfiguration) {
            return null;
        }

        const performanceRatings = configuration.performanceRatings.map(performanceRating =>
            new PerformanceRating(performanceRating.id, performanceRating.name));

        return new RatingConfiguration(
            cycleConfiguration.ratingConfiguration.showPerformanceRating,
            cycleConfiguration.ratingConfiguration.allowEditPerformanceRating,
            performanceRatings);
    }
}